// i18n/detectLanguage.ts — browser language detection + anonymous-visitor
// persistence for Phase A i18n.
//
// Two surfaces:
//   - Signed-in users: detect once on first visit, push to the server via
//     PUT /api/auth/language, show a confirming soft toast. Subsequent
//     visits read users.language from /api/auth/me and respect it.
//   - Anonymous visitors (public-reader on story.emberhollow.ai before
//     signup): detect once, persist to localStorage["ember.lang"]. Phase A
//     doesn't yet light up reader-translation (that's Phase C); the
//     storage scaffold lands here so Phase C can read it.

import {
  resolveSupportedLanguage,
  SUPPORTED_CODES,
} from "./supportedLanguages";

const ANON_STORAGE_KEY = "ember.lang";

/**
 * Detect the visitor's preferred narration language from browser signals.
 * Walks navigator.languages in order (most-preferred-first) and returns
 * the first entry that resolves to a supported BCP-47 code via
 * resolveSupportedLanguage's region-strip + Chinese-script logic.
 *
 * Returns null when none of the browser's preferred languages match the
 * Ember supported list. Callers then fall back to English UI + show the
 * "<lang> isn't supported yet — playing in English" soft toast.
 *
 * Defensive about non-browser / pre-rendering contexts — returns null
 * gracefully when navigator is undefined.
 */
export function detectBrowserLanguage(): string | null {
  if (typeof navigator === "undefined") return null;

  // navigator.languages is an ordered preference list (Chrome / Firefox /
  // Safari all support it). navigator.language is a single-string fallback
  // for older Safari + edge cases. Combine into a deduplicated walk.
  const tags: string[] = [];
  if (Array.isArray(navigator.languages)) {
    for (const tag of navigator.languages) {
      if (typeof tag === "string" && tag.length > 0) tags.push(tag);
    }
  }
  if (typeof navigator.language === "string" && navigator.language.length > 0) {
    tags.push(navigator.language);
  }

  const seen = new Set<string>();
  for (const tag of tags) {
    if (seen.has(tag)) continue;
    seen.add(tag);
    const resolved = resolveSupportedLanguage(tag);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Surface the first navigator preference even when it's NOT supported.
 * Used to compose the "your language isn't supported yet" toast — we
 * want to name the language the user actually asked for so the message
 * doesn't feel arbitrary. Returns null in headless / pre-render contexts.
 *
 * Conventionally we surface the base subtag (the part before the dash)
 * since "fr-FR" reads as "French" to the user, not "fr-FR".
 */
export function getPrimaryBrowserLanguageTag(): string | null {
  if (typeof navigator === "undefined") return null;
  let tag: string | null = null;
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    tag = navigator.languages[0] ?? null;
  } else if (typeof navigator.language === "string" && navigator.language.length > 0) {
    tag = navigator.language;
  }
  if (!tag) return null;
  const dash = tag.indexOf("-");
  return dash > 0 ? tag.slice(0, dash) : tag;
}

/**
 * Best-effort wrapper around localStorage.getItem. Returns null on
 * Safari-private-mode-throws / no-storage-available / disabled-cookies.
 */
function safeGet(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Load the anonymous visitor's stored language preference. Returns null
 * when the key is absent, the stored value isn't a supported code, or
 * localStorage is unavailable. The supported-code check means a stale
 * value from a deprecated language gracefully falls back to detection.
 */
export function loadAnonymousLanguage(): string | null {
  const raw = safeGet(ANON_STORAGE_KEY);
  if (!raw) return null;
  return SUPPORTED_CODES.has(raw) ? raw : null;
}

/**
 * Persist the anonymous visitor's chosen / detected language. Silently
 * no-ops when the code isn't supported (defensive against typos in
 * callers) or storage isn't available.
 */
export function saveAnonymousLanguage(code: string): void {
  if (!SUPPORTED_CODES.has(code)) return;
  safeSet(ANON_STORAGE_KEY, code);
}

/**
 * Clear the anonymous-visitor language. Mostly here for testing /
 * dev-tools symmetry — Phase A doesn't currently call this from app code.
 */
export function clearAnonymousLanguage(): void {
  safeRemove(ANON_STORAGE_KEY);
}

/**
 * Module-level flag preventing the App-level "first visit detection"
 * effect from firing more than once per page load. Survives StrictMode
 * double-mount (the flag lives on the module, not in component state).
 */
let firstVisitDetectionFired = false;

/** True the first time this is called in the page lifecycle; false thereafter. */
export function consumeFirstVisitDetectionToken(): boolean {
  if (firstVisitDetectionFired) return false;
  firstVisitDetectionFired = true;
  return true;
}
